/**
 * Wizard Context - State management for ModelWizard
 */
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { message } from 'antd';
import modelWizardAPI, { WizardStepData } from '../../services/modelWizardAPI';
import { adminAPI } from '../../services/api';
import type {
  AdminModelListResponse,
  AdminModelMeta,
  ModelCandidatePackageListResponse,
} from '../../types';
import { formatErrorMessage } from '../../utils/errorHandler';

interface WizardContextType {
  modelId: string;
  setModelId: (id: string) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  stepData: Record<number, WizardStepData>;
  updateStepData: (step: number, data: WizardStepData) => void;
  saveDraft: (step: number, data: WizardStepData) => Promise<WizardStepData>;
  submitModel: (steps?: Record<number, WizardStepData>) => Promise<void>;
  loadWizardData: (modelId: string) => Promise<void>;
  loading: boolean;
  isDraft: boolean;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
};

interface WizardProviderProps {
  children: ReactNode;
}

const getUploadOriginFile = (value: any): File | undefined => {
  const firstItem = Array.isArray(value) ? value[0] : value?.fileList?.[0];
  const candidate = firstItem ?? value;
  return candidate?.originFileObj ?? (candidate instanceof File ? candidate : undefined);
};

const getUploadFileBase = (value: any): string => {
  const firstItem = Array.isArray(value) ? value[0] : value?.fileList?.[0];
  const fileName = String(firstItem?.name || firstItem?.originFileObj?.name || '').trim();
  if (!fileName) {
    return '';
  }
  return fileName.replace(/\.[^.]+$/, '');
};

const inferUploadScriptFileBase = (...values: any[]): string => {
  for (const value of values) {
    const fileBase = getUploadFileBase(value);
    if (fileBase) {
      return fileBase;
    }
  }
  return '';
};

const stripStep1UploadFields = (data: WizardStepData) => {
  const {
    cpp_upload_list,
    r_upload_list,
    ...rest
  } = data || {};
  return rest;
};

export const stripStep1LifecycleFields = (data: WizardStepData) => {
  const {
    model_status,
    validation_status,
    review_status,
    qualification_status,
    candidate_qualification_status,
    qualification_version,
    qualified_at,
    ...rest
  } = data || {};
  return rest;
};

const stripStep1TransportFields = (data: WizardStepData) => {
  const {
    candidate_package,
    candidate_package_id,
    latest_qualification_run,
    ...rest
  } = stripStep1LifecycleFields(stripStep1UploadFields(data));
  return rest;
};

const isMissingDrugValue = (value: any) => {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  return !normalized || normalized === 'pending';
};

export const findFirstIncompleteStep = (steps: Record<number, WizardStepData>) => {
  const step1 = steps[1] || {};
  if (!String(step1.first_author || '').trim() || !step1.publication_year) {
    return {
      step: 1,
      message: '请先完善第 1 步的基本信息后再提交',
    };
  }
  const step2 = steps[2] || {};
  if (isMissingDrugValue(step2.drug_name)) {
    return {
      step: 2,
      message: '请先在第 2 步填写药物名称后再提交',
    };
  }

  return null;
};

export const mergeQualificationContext = (
  step1: WizardStepData,
  packagesResponse: ModelCandidatePackageListResponse,
  modelsResponse: AdminModelListResponse | AdminModelMeta[],
  modelId: string,
) => {
  const candidate = packagesResponse.items.find(
    (item) => item.model_id === modelId && !['withdrawn', 'archived'].includes(item.status),
  );
  const models = Array.isArray(modelsResponse) ? modelsResponse : modelsResponse.models;
  const modelMeta = models.find((item) => item.model_id === modelId);
  return {
    ...step1,
    candidate_package: candidate,
    candidate_package_id: candidate?.id,
    candidate_qualification_status: modelMeta?.qualification_status || 'not_started',
    review_status: modelMeta?.review_status || 'pending',
  };
};

export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
  const [modelId, setModelId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [stepData, setStepData] = useState<Record<number, WizardStepData>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isDraft, setIsDraft] = useState<boolean>(true);

  const updateStepData = (step: number, data: WizardStepData) => {
    setStepData(prev => ({ ...prev, [step]: data }));
  };

  const saveDraft = async (step: number, data: WizardStepData) => {
    if (!modelId && step !== 1) {
      message.error('请先填写步骤 1 的基本信息');
      throw new Error('请先填写步骤 1 的基本信息');
    }

    setLoading(true);
    try {
      let workingModelId = modelId || String(data.model_id || '').trim();
      let nextStepData = step === 1 ? stripStep1UploadFields(data) : data;

      if (step === 1) {
        const previousStep1 = stepData[1] || {};
        const cppFile = getUploadOriginFile(data.cpp_upload_list);
        const rFile = getUploadOriginFile(data.r_upload_list);
        const inferredUploadBase = inferUploadScriptFileBase(data.cpp_upload_list, data.r_upload_list);
        let nextScriptFileBase = String(data.script_file_base || '').trim() || inferredUploadBase;
        const previousScriptFileBase = String(previousStep1.script_file_base || '').trim();

        if (!workingModelId) {
          workingModelId = nextScriptFileBase;
        }

        if (!nextScriptFileBase) {
          nextScriptFileBase = workingModelId;
        }

        if (!workingModelId) {
          message.error('请先输入模型 ID');
          throw new Error('missing model id');
        }

        nextStepData = {
          ...nextStepData,
          model_id: workingModelId,
          script_file_base: nextScriptFileBase,
        };

        if (!modelId && (cppFile || rFile)) {
          const formData = new FormData();
          formData.append('model_id', workingModelId);
          formData.append('first_author', String(data.first_author || ''));
          formData.append('publication_year', String(data.publication_year || ''));
          formData.append('script_file_base', nextScriptFileBase);
          if (cppFile) formData.append('cpp_file', cppFile);
          if (rFile) formData.append('r_file', rFile);
          const uploadResponse = await modelWizardAPI.uploadDraft(formData);
          workingModelId = uploadResponse.model_id;
          setModelId(uploadResponse.model_id);
          nextStepData = {
            ...nextStepData,
            model_id: uploadResponse.model_id,
            script_file_base: uploadResponse.script_info?.file_base || nextScriptFileBase || workingModelId,
            script_info: uploadResponse.script_info,
          };
        } else if (
          modelId
          && (cppFile || rFile || (nextScriptFileBase && nextScriptFileBase !== previousScriptFileBase))
        ) {
          const formData = new FormData();
          if (nextScriptFileBase) {
            formData.append('script_file_base', nextScriptFileBase);
          }
          if (cppFile) formData.append('cpp_file', cppFile);
          if (rFile) formData.append('r_file', rFile);
          const uploadResponse = await modelWizardAPI.updateModelScripts(modelId, formData);
          nextStepData = {
            ...nextStepData,
            model_id: modelId,
            script_file_base: uploadResponse.script_info?.file_base || nextScriptFileBase || modelId,
            script_info: uploadResponse.script_info,
          };
        } else {
          nextStepData = {
            ...nextStepData,
            model_id: workingModelId,
          };
        }
      }

      const requestData = step === 1
        ? stripStep1TransportFields(nextStepData)
        : nextStepData;

      const response = await modelWizardAPI.saveWizardStep({
        model_id: workingModelId,
        step,
        draft: isDraft,
        data: requestData,
      });

      if (step === 1 && !modelId) {
        setModelId(response.model_id);
      }

      updateStepData(step, nextStepData);
      message.success(response.message);
      return nextStepData;
    } catch (error: any) {
      const errorMsg = formatErrorMessage(error, '保存失败');
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const submitModel = async (steps: Record<number, WizardStepData> = stepData) => {
    if (!modelId) {
      message.error('模型 ID 不存在');
      throw new Error('模型 ID 不存在');
    }

    const completionIssue = findFirstIncompleteStep(steps);
    if (completionIssue) {
      setCurrentStep(completionIssue.step - 1);
      message.warning(completionIssue.message);
      throw new Error(completionIssue.message);
    }

    setLoading(true);
    try {
      const response = await modelWizardAPI.submitModel(modelId);
      setIsDraft(false);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const targetStep = typeof detail?.step === 'number' ? detail.step : undefined;
      if (targetStep) {
        setCurrentStep(Math.max(0, targetStep - 1));
      }
      const errorMsg = formatErrorMessage(error, '提交失败');
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadWizardData = async (id: string) => {
    setLoading(true);
    try {
      const data = await modelWizardAPI.getWizardData(id);
      setModelId(data.model_id);
      setIsDraft(data.draft);
      setCurrentStep(Math.max(0, data.last_edited_step - 1));

      const loadedStepData: Record<number, WizardStepData> = {};
      if (data.step1) {
        let enrichedStep1 = data.step1;
        const [packagesResult, modelsResult] = await Promise.allSettled([
          adminAPI.listModelCandidatePackages(),
          adminAPI.listModelsMeta(),
        ]);
        enrichedStep1 = mergeQualificationContext(
          enrichedStep1,
          packagesResult.status === 'fulfilled'
            ? packagesResult.value
            : { items: [] },
          modelsResult.status === 'fulfilled'
            ? modelsResult.value
            : { models: [], total: 0 },
          id,
        );
        loadedStepData[1] = enrichedStep1;
      }
      if (data.step2) loadedStepData[2] = data.step2;
      if (data.step3) loadedStepData[3] = data.step3;
      if (data.step4) loadedStepData[4] = data.step4;
      if (data.step5) loadedStepData[5] = data.step5;
      if (data.step6) loadedStepData[6] = data.step6;
      if (data.step7) loadedStepData[7] = data.step7;

      setStepData(loadedStepData);
    } catch (error: any) {
      const errorMsg = formatErrorMessage(error, '加载模型数据失败');
      message.error(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardContext.Provider
      value={{
        modelId,
        setModelId,
        currentStep,
        setCurrentStep,
        stepData,
        updateStepData,
        saveDraft,
        submitModel,
        loadWizardData,
        loading,
        isDraft,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
